import { useCallback } from 'react';
import { DropResult } from '@hello-pangea/dnd';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import {
  createReorderContainersCommand,
  createUpdateContainerCommand,
  createBatchCommand,
  UndoableCommand,
} from '../../types/command.types';
import { ContainerAPI } from '../../types/state.types';
import { computeRowAwareReorder } from '../../container-management/utils/rowReorder';
import { ROOT_DROPPABLE_PREFIX, sortByRowThenOrder } from './pageContainersHelpers';

export const usePageContainerDragEnd = (
  canvasId: string,
  topLevelContainers: ContainerAPI[]
) => {
  const { dispatch, getContainer } = useUIModelState();

  return useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      // Drops only happen within the same list (rbd types are scoped per level).
      if (source.droppableId !== destination.droppableId) return;

      const isTopLevel = source.droppableId.startsWith(ROOT_DROPPABLE_PREFIX);
      const parentId = isTopLevel ? null : source.droppableId;

      // Resolve the sibling list for this level, sorted to match the rendered list.
      let siblings: ContainerAPI[];
      if (parentId === null) {
        siblings = topLevelContainers;
      } else {
        const { element: parent } = getContainer(parentId);
        const nested = parent?.content?.nestedContainers || [];
        siblings = [...nested].sort(sortByRowThenOrder);
      }

      if (siblings.length === 0) return;

      const movedId = siblings[source.index]?.id;
      if (!movedId) return;

      // ══════════════════════════════════════════════════════════════════════
      // Row-aware reorder — identical semantics for top-level AND nested
      // containers. The moved container ALWAYS keeps its own width. It JOINS a
      // row only when dropped strictly between two members of that row AND the
      // row has a free slot + enough free width to hold it; otherwise it moves
      // as its own new row at the drop position (existing members never shrink).
      // See computeRowAwareReorder for the decision table.
      // ══════════════════════════════════════════════════════════════════════
      const { newIds, assignments } = computeRowAwareReorder(
        siblings,
        movedId,
        destination.index
      );

      const reorderCommand = createReorderContainersCommand(
        { canvasId, parentId, containerIds: newIds },
        'Reorder containers in right panel'
      );

      // No rowIndex/width changes (pure same-row column reorder) → one command.
      if (assignments.length === 0) {
        dispatch({ type: 'EXECUTE_COMMAND', payload: reorderCommand });
        return;
      }

      const subCommands: UndoableCommand[] = [
        reorderCommand,
        ...assignments.map((a) =>
          createUpdateContainerCommand(
            {
              containerId: a.containerId,
              canvasId,
              updates: {
                rowIndex: a.rowIndex,
                metadata: { rowIndex: a.rowIndex },
                configuration: {
                  width: a.width,
                },
              },
            },
            `Move ${a.containerId} to row ${a.rowIndex}`
          )
        ),
      ];

      dispatch({
        type: 'EXECUTE_COMMAND',
        payload: createBatchCommand(subCommands, 'Reorder containers'),
      });
    },
    [canvasId, topLevelContainers, getContainer, dispatch]
  );
};
